// Workflow-automatisering (blok C): oplevering en eindfactuur van een project uit Bouwflow.
// Pure functies, gedeeld tussen server (polling, filters, mails) en web (kenmerken op de calculatie).

/** Status van een opleveringsbon in Bouwflow (delivery-notes). */
export type OpleveringsbonStatus = 'ok' | 'provisional' | 'denied';

export interface OpleveringInfo {
  id: number | null;
  status: OpleveringsbonStatus | null;
  /** Datum van de bon. */
  datum: string | null;
  /** Datum waarop de klant tekende; null = niet door de klant ondertekend. */
  getekendOp: string | null;
  /** Opmerking bij een voorlopige of geweigerde oplevering. */
  reden: string | null;
}

export type EindfactuurStatus = 'betaald' | 'verstuurd' | 'geen';

/** Factuurtype 3 in Bouwflow = eindfactuur. */
export const EINDFACTUUR_TYPE = 3;

const NIET_VERSTUURD = new Set(['draft', 'to_be_sent', 'credited']);

/** Eindfactuur aan de klant: betaald, verstuurd (nog niet betaald) of nog geen. Gecrediteerd telt als geen. */
export function eindfactuurStatus(facturen: ReadonlyArray<{ type: number | null; status: string }>): EindfactuurStatus {
  const eind = facturen.filter((f) => f.type === EINDFACTUUR_TYPE && !NIET_VERSTUURD.has(f.status));
  if (!eind.length) return 'geen';
  return eind.some((f) => f.status === 'paid') ? 'betaald' : 'verstuurd';
}

/** Een opleveringsbon telt als oplevering wanneer hij "ok" is en door de klant ondertekend (SPEC_workflow 3.1). */
export function isOndertekendeOplevering(o: Pick<OpleveringInfo, 'status' | 'getekendOp'> | null | undefined): boolean {
  return !!o && o.status === 'ok' && !!o.getekendOp;
}

/**
 * De bon die het project typeert: de laatste ondertekende ok-bon, anders de laatste bon.
 * Een project kan eerst een voorlopige of geweigerde oplevering hebben en later een definitieve.
 */
export function kiesOplevering(bonnen: ReadonlyArray<OpleveringInfo>): OpleveringInfo | null {
  if (!bonnen.length) return null;
  const volgorde = (a: OpleveringInfo, b: OpleveringInfo) => (b.datum ?? '').localeCompare(a.datum ?? '') || (b.id ?? 0) - (a.id ?? 0);
  const getekend = bonnen.filter(isOndertekendeOplevering).sort(volgorde);
  return getekend[0] ?? [...bonnen].sort(volgorde)[0]!;
}

export interface WorkflowStaat {
  oplevering: OpleveringInfo | null;
  eindfactuur: EindfactuurStatus;
  /** Opgeleverd: ondertekende bon, of een betaalde eindfactuur zonder bon (SPEC_workflow 3.2). */
  opgeleverd: boolean;
  opgeleverdVia: 'opleveringsbon' | 'eindfactuur' | null;
}

export function workflowStaat(oplevering: OpleveringInfo | null, facturen: ReadonlyArray<{ type: number | null; status: string }>): WorkflowStaat {
  const eindfactuur = eindfactuurStatus(facturen);
  const bon = isOndertekendeOplevering(oplevering);
  return {
    oplevering,
    eindfactuur,
    opgeleverd: bon || eindfactuur === 'betaald',
    opgeleverdVia: bon ? 'opleveringsbon' : eindfactuur === 'betaald' ? 'eindfactuur' : null,
  };
}

export const OPLEVERINGSBON_LABEL: Record<OpleveringsbonStatus, string> = {
  ok: 'Opgeleverd',
  provisional: 'Voorlopige oplevering',
  denied: 'Oplevering geweigerd',
};

export const EINDFACTUUR_LABEL: Record<EindfactuurStatus, string> = {
  betaald: 'Eindfactuur betaald',
  verstuurd: 'Eindfactuur verstuurd',
  geen: 'Nog geen eindfactuur',
};

/** Soort melding uit de polling. */
export type WorkflowSoort = 'opgeleverd' | 'eindfactuur_betaald';

/**
 * Wat er voor het project moet gebeuren (SPEC_workflow 9):
 *  - te_calculeren: opgeleverd, nog geen calculatie, opstartfiche gevonden
 *  - geen_opstartfiche: opgeleverd, nog geen calculatie, geen opstartfiche in Bouwflow
 *  - calculatie_opgemaakt: de tool maakte een conceptcalculatie uit de opstartfiche (3.1)
 *  - vrijgave: er bestaat een calculatie met posten open voor oplevering (6.4)
 *  - volledig: er bestaat een volledige calculatie, niets te doen
 */
export type WorkflowSituatie = 'te_calculeren' | 'geen_opstartfiche' | 'calculatie_opgemaakt' | 'vrijgave' | 'volledig';

export const WORKFLOW_SITUATIE_LABEL: Record<WorkflowSituatie, string> = {
  te_calculeren: 'Te calculeren',
  geen_opstartfiche: 'Geen opstartfiche',
  calculatie_opgemaakt: 'Conceptcalculatie opgemaakt',
  vrijgave: 'Open posten vrij te geven',
  volledig: 'Al volledig gecalculeerd',
};

export const WORKFLOW_SOORT_LABEL: Record<WorkflowSoort, string> = {
  opgeleverd: 'Opleveringsbon ondertekend',
  eindfactuur_betaald: 'Eindfactuur betaald, geen opleveringsbon',
};

export interface WorkflowMelding {
  id: string;
  soort: WorkflowSoort;
  bronId: number;
  projectNummer: string;
  bouwflowProjectId: number | null;
  klantNaam: string;
  situatie: WorkflowSituatie;
  details: {
    projectTitel?: string;
    projectType?: string | null;
    /** Open posten bij een vrijgave (6.4). */
    openPosten?: Array<{ calcId: string; naam: string; bedrag: number }>;
    openBedrag?: number;
    /** Datum waarop de calculatie met open posten verstuurd werd. */
    verstuurdOp?: string | null;
    /** Percentagecalculatie (6.4, variant): al gecalculeerd percentage, het restpercentage en zijn bedrag. */
    percentage?: { gefactureerd: number; rest: number; bedrag: number } | null;
    calculaties?: Array<{ id: string; totaal: number; ploeg: string }>;
    reden?: string | null;
    fout?: string;
  };
  calcId: string | null;
  gebeurdOp: string | null;
  aangemaaktOp: string;
  gemaildOp: string | null;
  mailFout: string | null;
  afgehandeldOp: string | null;
  afgehandeldDoor: string | null;
}
